import * as React from 'react';
import { Icon, Popup, Button } from 'semantic-ui-react';
import { connect } from 'react-redux';

import { tl } from 'common/services/i18nService';
import * as workspaceActions from 'common/store/workspaces/actions';
import {
    WorkspaceOverlayConfiguration,
    WorkspacesOverlayTargets,
    WorkspacesOverlayTypes,
} from 'common/store/workspaces/types';

import * as sessionActions from 'common/store/session/actions';
import { SessionState } from 'common/store/session/reducer';
import { WorkspacesState } from 'common/store/workspaces/reducer';

import * as styles from 'common/components/header/Header.scss';
import navigationListService from '../../services/navigationListService';
import { AssessmentState } from '../../store/assessment/reducer';
import { ContextState } from '../../store/context/reducer';
import HeaderRight from './HeaderRight';
import { HeaderLeft } from './HeaderLeft';
import { RootState } from '../../store/rootReducer';
import * as contextActions from '../../store/context/actions';

import { hasCurrentUserPlannerPrivilege } from '../../services/permissions/PermissionService';
import { syncWithExternalTest } from '../../store/syncButton/actions';
import { isSyncAllowedForAssessmentRun } from './services/externalSystemService';

interface StateProps {
    assessment?: AssessmentState;
    context?: ContextState;
    session?: SessionState;
    workspaces?: WorkspacesState;
}

interface HeaderProps extends StateProps {
    setLanguage?: (language: string) => void;
    setWorkspaceOverlay?: (workspaceOverlayName: string,
                           targetNodeId: string,
                           workspaceOverlayConfiguration: WorkspaceOverlayConfiguration) => void;
    toggleDetailsPanel?: () => void;
    syncWithExternalTest?: () => void;
}

class HeaderComponent extends React.PureComponent<HeaderProps> {
    onBackToListOfTests = () => {
        const { context } = this.props;
        navigationListService.backToOriginalLists(context);
    };

    openCommitteeOverlay = () => {
        const { setWorkspaceOverlay, context } = this.props;
        const isNotClosable = !hasCurrentUserPlannerPrivilege() && context.selectedCommitteeId < 0;
        setWorkspaceOverlay(WorkspacesOverlayTypes.COMMITTEE_SELECTOR, WorkspacesOverlayTargets.APP_OVERLAY_TARGET, {
            options: { isNotClosable },
            workspaceTitle: isNotClosable ? tl('grading.overlay.committeeSelector.notClosable.title')
                : tl('grading.overlay.committeesSelector.title'),
        });
    };

    openPhaseOverlay = () => {
        const { setWorkspaceOverlay } = this.props;
        setWorkspaceOverlay(WorkspacesOverlayTypes.PHASE_SELECTOR, WorkspacesOverlayTargets.APP_OVERLAY_TARGET, {
            options: { isNotClosable: false },
            workspaceTitle: tl('grading.overlay.phaseSelector.title'),
        });
    };

    openIssuesOverlay = () => {
        const { setWorkspaceOverlay, assessment } = this.props;
        const isNotClosable = false;
        const { isLoadingExternalTestSync, externalSystem } = assessment;
        setWorkspaceOverlay(WorkspacesOverlayTypes.POSSIBLE_ISSUES, WorkspacesOverlayTargets.APP_OVERLAY_TARGET, {
            options: {
                isNotClosable,
                iconTitle: (<Icon name="flag outline" size="large" />),
                actionButtonRight: isSyncAllowedForAssessmentRun() ? (
                    <Button
                        basic
                        className={styles.syncButtonOverlay}
                        onClick={this.onSyncWithExternalTest}
                        loading={isLoadingExternalTestSync}
                    >
                        <Icon name="redo" />
                        {externalSystem}
                    </Button>
                ) : null,
            },
            workspaceTitle: `${tl('grading.header.issuesOverlayTitle')}`,
        });
    };

    onSyncWithExternalTest = () => {
        this.props.syncWithExternalTest();
    };

    render() {
        const {
            context, assessment, session, setLanguage, toggleDetailsPanel,
        } = this.props;
        const { privileges } = session.user;

        return (
            <div className={styles.headerWrapper} lang={session.language}>
                <div className={styles.topBar} role="banner" aria-label={tl('grading.header.markerOptions')}>
                    <Popup
                        trigger={(
                            <a
                                href="javascript:void(0)"
                                onClick={this.onBackToListOfTests}
                                className={styles.backButton}
                                title={tl('grading.backToList.message')}
                            >
                                <Icon name="angle left" size="large" />
                            </a>
                        )}
                        content={tl('grading.backToList.message')}
                        position="bottom left"
                    />


                    {
                        privileges
                        && (
                            <HeaderLeft
                                assessment={assessment}
                                context={context}
                                session={session}
                                openCommitteeOverlay={this.openCommitteeOverlay}
                                openPhaseOverlay={this.openPhaseOverlay}
                            />
                        )
                    }
                    <HeaderRight
                        setLanguage={setLanguage}
                        assessment={assessment}
                        context={context}
                        session={session}
                        onToggleDetailsPanel={toggleDetailsPanel}
                        openIssuesOverlay={this.openIssuesOverlay}
                    />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    return {
        assessment: state.assessment,
        context: state.context,
        session: state.session,
        workspaces: state.workspaces,
    };
};

export default connect<StateProps>(mapStateToProps, {
    setLanguage: sessionActions.setLanguage,
    setWorkspaceOverlay: workspaceActions.setWorkspaceOverlay,
    toggleDetailsPanel: contextActions.toggleDetailsPanel,
    syncWithExternalTest,
})(HeaderComponent);
